import { css } from '@emotion/core';
import { BrowserUtils } from '../../utils';

const smallViewportStyles = css`
  @media (max-width: 320px) {
    width: 100%;

    /* for improved spacing when wrapping/stacking: */
    margin: 0 0 .25rem 0 !important;

    /* if a button is placed in a large container (which can happen due to
     * interactions with table) the button shouldnt be absurdly large.
     * 288px is probably the largest a button can be after padding.
     */
    max-width: 288px;
  }
`;

const highContrastStylesBase = color => css`
  button .vds-icon {
    fill: ${color} !important;
  }
`;
const highContrastStyles = css`
  @media screen and (-ms-high-contrast: active) {
    ${highContrastStylesBase('white')}
  }

  @media screen and (-ms-high-contrast: black-on-white) {
    ${highContrastStylesBase('black')}
  }

  ${BrowserUtils.isHighContrastMode()
    ? highContrastStylesBase('black')
    : ''}
`;

const sentenceCaseStyles = `
  text-transform: lowercase;
  span::first-letter {
    text-transform: uppercase;
  }
`;

const getBaseBtnStyles = ({ cybs }) => `
  ${sentenceCaseStyles}
  letter-spacing: 0px;
  &.vds-state--disabled {
    background-color: ${cybs?.base?.wcag21?.disabledGrey ?? '#00000033'};
  }
`;

const getActiveBtnStyles = ({ cybs }) => `
  &:active {
    background-color: ${cybs?.core?.primaryDark ?? '#AAAAAA'};
    border-color: ${cybs?.core?.primaryDark ?? '#0F2595'};
  }
`;

const getIconActiveBtnStyles = ({ cybs, vds }) => `
  & {
    background-color: ${vds?.primaryExtraDark2 ?? '#021E4C'} !important;
    border: none;

    [class^='vds-icon'] use {
      fill: ${cybs?.base?.wcag21?.white ?? '#FFFFFF'} !important;
    }
  }
`;

const getPrimaryBtnStyles = ({ cybs }) => `
  ${getBaseBtnStyles({ cybs })}
  border: 2px solid transparent;
  background-color: ${cybs?.core?.primary ?? '#1434cb'};

  &:hover, &:focus {
    background-color: ${cybs?.core?.primaryDark ?? '#0F2595'};
  }
  ${getActiveBtnStyles({ cybs })}
`;

const getSecondaryBtnStyles = ({ cybs, vds }) => `
  ${getBaseBtnStyles({ cybs })}
  border-color: ${cybs?.core?.primary ?? '#1434cb'};
  color: ${cybs?.base?.wcag21?.charcoal ?? '#000000'};

  & [class^='vds-icon'] use {
    fill: ${cybs?.core?.primary ?? '#1434cb'};
  }

  &:hover,
  &:focus {
    /* TODO: replace this color with a color from the cybs theme */
    background-color: ${vds?.tableHighlight ?? '#e0eafa'};
    border-color: ${cybs?.core?.primary ?? '#1434cb'};
    color: ${cybs?.base?.wcag21?.charcoal ?? '#000000'};

    & [class^='vds-icon'] use {
      fill: ${cybs?.core?.primary ?? '#1434cb'};
    }
  }
  &:active {
    ${getActiveBtnStyles({ cybs })}
    color: ${cybs?.base?.vault?.white ?? '#ffffff'};
    & [class^='vds-icon'] use {
      fill: ${cybs?.base?.vault?.white ?? '#ffffff'};
    }
  }

  &.vds-state--disabled {
    background-color: ${cybs?.base?.vault?.white ?? '#ffffff'};
    color: ${cybs?.base?.wcag21?.disabledGrey ?? '#00000033'};
    border-color: ${cybs?.base?.wcag21?.disabledGrey ?? '#00000033'};

    & [class^='vds-icon'] use {
      fill: ${cybs?.base?.wcag21?.disabledGrey ?? '#00000033'};
    }
  }
`;

// Divider and icon styling for split buttons
const getButtonActionStyles = (color, cybs) => `
    &:not(.vds-state--disabled) .vds-btn--action {
      &:last-child {
        border-color: ${color};
      }

      & [class^='vds-icon'] use {
        fill: ${color};
      }
    }

    &.vds-state--disabled .vds-btn--action {
      border-color: ${cybs?.base?.wcag21?.disabledGrey ?? '#00000033'};
    }
  `;

const stylesBuilder = ({ width, diffLabel }) => ({
  splitButtonStyles: ({ cybs, vds }) => css`
    ${smallViewportStyles}
    ${highContrastStyles}

    &.vds-btn-split, &.vds-btn-split--primary, &.vds-btn-split--secondary {
      display: inline-flex;
      white-space: initial;

      & > span, & > span:first-child, & > span:last-child {
        word-break: break-word;
        /* to ensure the text isn't too close to the sides on wrap: */
        padding: 0.625rem 1rem;
        /* make sure that the spans can be resized for wrapping to occur: */
        flex: initial;
      }

      & > .vds-btn--action {
        /* to more-easily center the icon: */
        display: flex !important;
        /* to ensure that icons do not shrink: */
        min-width: 3rem;
      }
    }

    &.vds-btn-split--primary {
      ${getPrimaryBtnStyles({ cybs })}
    }

    &.vds-btn-split--secondary {
      ${getSecondaryBtnStyles({ cybs, vds })}
      ${getButtonActionStyles(cybs?.core?.primary ?? '#1434cb', cybs)}
        &:active {
        ${getButtonActionStyles(cybs?.base?.vault?.white ?? '#ffffff', cybs)}

        & .vds-btn--action:last-child {
          border-left: 1px solid ${cybs?.base?.wcag21?.white ?? '#FFFFFF'};
        }
      }
    }
  `,
  iconButtonStyles: ({ cybs, vds }) => css`
    &.vds-btn-icon--light:hover:not(:active),
    &.vds-btn-icon--light:focus:not(:active),
    &.vds-btn-icon--light-tiny:hover:not(:active),
    &.vds-btn-icon--light-tiny:focus:not(:active),
    &.vds-btn-icon--light-outline:hover:not(:active),
    &.vds-btn-icon--light-outline:focus:not(:active) {
      background-color: ${vds?.tableHighlight ?? '#C7EDFF'};
      [class^='vds-icon'] use {
        fill: ${vds?.primaryExtraDark2 ?? '#021E4C'};
      }
    }
    
    &.vds-btn-icon--light:hover:not(:active):not(:focus),
    &.vds-btn-icon--light-tiny:hover:not(:active):not(:focus),
    &.vds-btn-icon--light-outline:hover:not(:active):not(:focus) {
      border: 0.1px solid ${vds?.primaryExtraDark2 ?? '#021E4C'};
    }
    
    &.vds-btn-icon--light:focus:not(:active):not(:hover),
    &.vds-btn-icon--light-tiny:focus:not(:active):not(:hover),
    &.vds-btn-icon--light-outline:focus:not(:active):not(:hover) {
      outline: 1px dashed ${vds?.primaryExtraDark2 ?? '#021E4C'};
    }

    &.vds-btn-icon--light:focus:not(:active),
    &.vds-btn-icon--light:hover {
      [class^='vds-icon'] use {
        fill: ${vds?.primaryExtraDark2 ?? '#021E4C'} !important;
      }
    }

    &.vds-btn-icon--light:active:hover {
      [class^='vds-icon'] use {
        fill: ${cybs?.base?.wcag21?.white ?? '#FFFFFF'} !important;
      }
    }

    &.vds-btn-icon--light:active,
    &.vds-btn-icon--light-tiny:active,
    &.vds-btn-icon--light-outline:active {
      ${getIconActiveBtnStyles({ cybs, vds })}
    }
    &.vds-btn-icon--light:disabled,
    &.vds-btn-icon--light-outline:disabled,
    &.vds-btn-icon--light-tiny:disabled {
      border-color: ${cybs?.base?.wcag21?.disabledGrey ?? '#00000033'};
      
      [class^='vds-icon'] use {
        fill: ${cybs?.base?.wcag21?.disabledGrey ?? '#00000033'};
      }
    }
    use, span {
      pointer-events: none;
    }
    &.vds-btn-icon:hover:not(:active) {
      background-color: ${vds?.tableHighlight ?? '#C7EDFF'} !important;
    }
  
    margin-left: 0;
    margin-right: 0;
  `,
  textButtonStyles: ({ classes, cybs, vds }) => css`
    ${smallViewportStyles}
    ${highContrastStyles}

    &.vds-btn-text--primary {
      ${getPrimaryBtnStyles({ cybs })}
    }

    &.vds-btn-text--secondary,
    &.vds-btn-text--tertiary {
      ${getSecondaryBtnStyles({ cybs, vds })}
    }

    & > span.vds-btn-label {
      padding: 0;
      /* fix text wrapping: */
      white-space: initial !important;
      word-break: break-word;

      & > span {
        /* to ensure the text isn't too close to the top on wrap: */
        padding: 0.625rem 0;
      }

      & > svg {
        /* to ensure that icons do not shrink: */
        min-width: 1.5rem;
      }

      & > span:nth-child(1) {
        ${classes?.structure?.margin?.space?.left[2] ?? 'margin-left: 1rem;'}
      }

      & > span:nth-last-child(${diffLabel ? 2 : 1}) {
        ${classes?.structure?.margin?.space?.right[2] ?? 'margin-right: 1rem;'}
      }

      & > span:nth-child(1):nth-last-child(${diffLabel ? 2 : 1}) {
        margin: 0 1rem;
      }

      & > svg:nth-child(1) {
        margin-left: 1rem;
        margin-right: 0.5rem !important;
      }

      & > svg:nth-last-child(${diffLabel ? 2 : 1}) {
        margin-right: 1rem;
        margin-left: 0.5rem;
      }
    }
  `,
  linkButtonStyles: ({ cybs, variables }) => css`
    ${width ? `width: ${width};` : ''}
    ${width ? 'word-break: break-word !important; word-wrap: break-word;' : ''}
      ${width ? 'white-space: pre-wrap !important;' : ''}

    &.vds-btn-link[disabled],
    &.vds-btn-link--primary[disabled],
    &.vds-btn-link--secondary[disabled] {
      color: ${variables?.CSS_VARS?.linkColorDisabled ?? '#b5b5b5'};
    }

    &.vds-btn-link--primary,
    &.vds-btn-link--primary:link,
    &.vds-btn-link--primary:hover,
    &.vds-btn-link--primary:active,
    &.vds-btn-link--primary:focus,
    &.vds-btn-link--primary:visited,
    &.vds-btn-link--secondary,
    &.vds-btn-link--secondary:link,
    &.vds-btn-link--secondary:hover,
    &.vds-btn-link--secondary:active,
    &.vds-btn-link--secondary:focus,
    &.vds-btn-link--secondary:visited {
      letter-spacing: 0;
      text-decoration: underline;
      text-transform: none;
      color: ${cybs?.core?.primaryDark ?? '#0F2595'};
    }
  `
});

export default stylesBuilder;
